import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import type { AdminCategory, AdminPhrase } from '../../types';

export function PhrasesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get('categoryId') ?? '';
  const [rows, setRows] = useState<AdminPhrase[] | null>(null);
  const [cats, setCats] = useState<AdminCategory[]>([]);
  const [error, setError] = useState<string | null>(null);

  async function reload() {
    try {
      const url = filter ? `/api/admin/phrases?categoryId=${encodeURIComponent(filter)}` : '/api/admin/phrases';
      const [phrases, categories] = await Promise.all([
        api<AdminPhrase[]>(url),
        api<AdminCategory[]>('/api/admin/categories'),
      ]);
      setRows(phrases);
      setCats(categories);
    } catch (err) { setError(err instanceof Error ? err.message : 'Load failed'); }
  }
  useEffect(() => { void reload(); }, [filter]);

  async function onDelete(id: string, vi: string) {
    if (!confirm(`Delete phrase "${vi}"?`)) return;
    try { await api(`/api/admin/phrases/${id}`, { method: 'DELETE' }); await reload(); }
    catch (err) { alert(err instanceof Error ? err.message : 'Failed'); }
  }

  return (
    <section className="admin-page">
      <header className="admin-page-header">
        <h1>Phrases</h1>
        <Link to="/admin/phrases/new" className="btn-primary">New phrase</Link>
      </header>
      <div className="form-group" style={{ maxWidth: 320 }}>
        <label>Filter by category</label>
        <select className="admin-select" value={filter} onChange={(e) => setSearchParams(e.target.value ? { categoryId: e.target.value } : {})}>
          <option value="">All categories</option>
          {cats.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
        </select>
      </div>
      {error && <div className="form-error">{error}</div>}
      {!rows && !error && <p style={{ color: 'var(--text-muted)' }}>Loading…</p>}
      {rows && (
        <table className="admin-table">
          <thead>
            <tr><th>Vietnamese</th><th>English</th><th>Category</th><th>#</th><th>Audio</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id}>
                <td>{p.vietnamese}</td>
                <td>{p.english}</td>
                <td>{p.categoryName}</td>
                <td>{p.sortOrder}</td>
                <td><code>{p.audioFile}</code></td>
                <td className="admin-table-actions">
                  <Link to={`/admin/phrases/${p.id}`} className="btn-link">Edit</Link>
                  <button type="button" className="btn-link" onClick={() => void onDelete(p.id, p.vietnamese)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

interface PhraseForm {
  categoryId: string;
  vietnamese: string;
  phonetic: string;
  english: string;
  situationTag: string;
  contextNote: string;
  audioFile: string;
  sortOrder: number;
}
const EMPTY: PhraseForm = {
  categoryId: '', vietnamese: '', phonetic: '', english: '',
  situationTag: '', contextNote: '', audioFile: '', sortOrder: 0,
};

export function PhraseEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const [data, setData] = useState<PhraseForm>(EMPTY);
  const [cats, setCats] = useState<AdminCategory[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const categories = await api<AdminCategory[]>('/api/admin/categories');
        setCats(categories);
        if (!isNew) {
          const p = await api<PhraseForm>(`/api/admin/phrases/${id}`);
          setData({ ...p, situationTag: p.situationTag ?? '', contextNote: p.contextNote ?? '' });
        }
      } catch (err) { setError(err instanceof Error ? err.message : 'Load failed'); }
    })();
  }, [id, isNew]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const body = JSON.stringify({
        ...data,
        situationTag: data.situationTag || null,
        contextNote: data.contextNote || null,
      });
      if (isNew) await api('/api/admin/phrases', { method: 'POST', body });
      else await api(`/api/admin/phrases/${id}`, { method: 'PUT', body });
      navigate('/admin/phrases');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally { setSubmitting(false); }
  }

  return (
    <section className="admin-page">
      <header className="admin-page-header">
        <h1>{isNew ? 'New phrase' : 'Edit phrase'}</h1>
      </header>
      {error && <div className="form-error">{error}</div>}
      <form onSubmit={(e) => void onSubmit(e)} className="admin-form">
        <div className="form-group">
          <label>Category</label>
          <select className="admin-select" required value={data.categoryId} onChange={(e) => setData({ ...data, categoryId: e.target.value })} disabled={submitting}>
            <option value="">— select —</option>
            {cats.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
          </select>
        </div>
        <div className="form-row-pair">
          <div className="form-group">
            <label>Vietnamese</label>
            <input type="text" required value={data.vietnamese} onChange={(e) => setData({ ...data, vietnamese: e.target.value })} disabled={submitting} />
          </div>
          <div className="form-group">
            <label>Phonetic</label>
            <input type="text" required value={data.phonetic} onChange={(e) => setData({ ...data, phonetic: e.target.value })} disabled={submitting} />
          </div>
        </div>
        <div className="form-group">
          <label>English</label>
          <input type="text" required value={data.english} onChange={(e) => setData({ ...data, english: e.target.value })} disabled={submitting} />
        </div>
        <div className="form-row-pair">
          <div className="form-group">
            <label>Situation tag</label>
            <input type="text" value={data.situationTag} onChange={(e) => setData({ ...data, situationTag: e.target.value })} disabled={submitting} />
          </div>
          <div className="form-group">
            <label>Audio file</label>
            <input type="text" required value={data.audioFile} onChange={(e) => setData({ ...data, audioFile: e.target.value })} disabled={submitting} placeholder="phrase_sf_007.mp3" />
          </div>
        </div>
        <div className="form-group">
          <label>Context note</label>
          <input type="text" value={data.contextNote} onChange={(e) => setData({ ...data, contextNote: e.target.value })} disabled={submitting} />
        </div>
        <div className="form-group">
          <label>Sort order</label>
          <input type="number" value={data.sortOrder} onChange={(e) => setData({ ...data, sortOrder: Number(e.target.value) })} disabled={submitting} style={{ maxWidth: 120 }} />
        </div>
        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={() => navigate('/admin/phrases')} disabled={submitting}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={submitting}>{submitting ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </section>
  );
}
